<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">结算单查询</h1>
            <p class="list-page-description">查询结算单记录，查看分账明细和状态</p>
          </div>
        </div>
      </div>
    </template>

    <!-- 表格区域 -->
    <div class="list-page-table-panel">
      <PageFilterBar
        shell-class="filter-bar-shell is-embedded"
        body-class="filter-bar-body is-panel"
        :show-refresh="true"
        :refresh-loading="loading"
        @refresh="fetchRecords"
      >
        <div class="filter-bar-field is-wide relative">
          <input
            v-model="searchForm.settlementNo"
            type="text"
            placeholder="请输入结算单号"
            class="form-search-input form-control-sm w-full"
            @keyup.enter="handleSearch"
          >
          <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </div>
        <el-select
          v-model="searchForm.sourceType"
          placeholder="请选择类型"
          class="form-select form-control-sm filter-bar-field"
        >
          <el-option label="全部类型" value="" />
          <el-option v-for="t in sourceTypes" :key="t.value" :label="t.label" :value="t.value" />
        </el-select>
        <el-select
          v-model="searchForm.status"
          placeholder="请选择状态"
          class="form-select form-control-sm filter-bar-field"
        >
          <el-option label="全部状态" value="" />
          <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
        <template #actions>
          <button
            type="button"
            class="btn-secondary"
            @click="showAdvancedFilter = !showAdvancedFilter"
          >
            {{ showAdvancedFilter ? '收起筛选' : '高级筛选' }}
          </button>
        </template>

        <template #searchActions>
          <button type="button" class="btn-primary" @click="handleSearch">搜索</button>
          <button type="button" class="btn-secondary" @click="resetSearch">重置</button>
        </template>

        <template #extra>
          <div v-if="showAdvancedFilter" class="border-t border-slate-200 pt-4">
            <div class="flex flex-wrap gap-3 items-center">
              <input
                v-model="searchForm.sourceId"
                type="text"
                inputmode="numeric"
                placeholder="来源ID（精确匹配）"
                class="form-input form-control-sm filter-bar-field"
              >
              <input
                v-model="searchForm.orderId"
                type="text"
                inputmode="numeric"
                placeholder="订单ID（精确匹配）"
                class="form-input form-control-sm filter-bar-field"
              >
              <el-date-picker
                v-model="searchForm.startTime"
                type="datetime"
                format="YYYY年MM月DD日 HH:mm"
                value-format="YYYY-MM-DD HH:mm:ss"
                placeholder="分账开始时间"
                class="filter-bar-field form-date form-control-sm"
              />
              <el-date-picker
                v-model="searchForm.endTime"
                type="datetime"
                format="YYYY年MM月DD日 HH:mm"
                value-format="YYYY-MM-DD HH:mm:ss"
                placeholder="分账结束时间"
                class="filter-bar-field form-date form-control-sm"
              />
              <el-select
                v-model="searchForm.ruleId"
                class="form-select form-control-sm filter-bar-field"
              >
                <el-option label="全部规则" value="" />
                <el-option v-for="rule in ruleList" :key="rule.id" :label="rule.name" :value="rule.id" />
              </el-select>
            </div>
          </div>
        </template>
      </PageFilterBar>
      <div class="data-table-shell">
        <div class="data-table-scroll">
          <table class="data-table text-sm">
            <thead class="data-table-head">
              <tr>
                <th class="data-table-th text-left">结算单号</th>
                <th class="data-table-th text-left">来源编号</th>
                <th class="data-table-th text-left">来源</th>
                <th class="data-table-th text-right">总金额</th>
                <th class="data-table-th text-left">分账状态</th>
                <th class="data-table-th text-left">分账时间</th>
                <th class="data-table-th text-center w-32">操作</th>
              </tr>
            </thead>
            <tbody>
              <!-- 骨架屏加载状态 -->
              <template v-if="loading">
                <tr v-for="n in 5" :key="n" class="animate-pulse">
                  <td class="data-table-td"><div class="h-4 w-32 bg-slate-200 rounded"/></td>
                  <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"/></td>
                  <td class="data-table-td is-center"><div class="h-6 w-16 bg-slate-200 rounded-full"/></td>
                  <td class="data-table-td is-right"><div class="h-4 w-16 bg-slate-200 rounded ml-auto"/></td>
                  <td class="data-table-td is-center"><div class="h-6 w-20 bg-slate-200 rounded-full"/></td>
                  <td class="data-table-td"><div class="h-4 w-24 bg-slate-200 rounded"/></td>
                  <td class="data-table-td is-center"><div class="h-8 w-20 bg-slate-200 rounded"/></td>
                </tr>
              </template>
              <!-- 空状态 -->
              <tr v-if="!loading && recordList.length === 0">
                <td colspan="7" class="data-table-empty">
                  <div class="flex flex-col items-center">
                    <svg class="w-12 h-12 text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                    <span>暂无结算单</span>
                  </div>
                </td>
              </tr>
              <tr v-for="record in recordList" :key="record.id" class="data-table-row">
                <td class="data-table-td font-mono">{{ record.settlementNo }}</td>
                <td class="data-table-td font-mono">{{ record.orderNo || record.sourceId }}</td>
                <td class="data-table-td">
                  <span class="px-2 py-0.5 rounded text-xs" :class="getSourceTypeClass(record.sourceType)">
                    {{ getSourceTypeLabel(record.sourceType) }}
                  </span>
                </td>
                <td class="data-table-td is-right font-medium">¥{{ record.totalAmount?.toLocaleString() }}</td>
                <td class="data-table-td">
                  <span class="status-badge" :class="getStatusClass(record.status)">
                    <span class="w-1.5 h-1.5 rounded-full mr-1" :class="getStatusDotClass(record.status)"/>
                    {{ record.statusDesc || getStatusLabel(record.status) }}
                  </span>
                </td>
                <td class="data-table-td text-slate-600">{{ record.settleTime || '-' }}</td>
                <td class="data-table-td is-center">
                  <div class="flex items-center justify-center gap-1">
                    <button class="table-action-button is-primary" title="查看详情" aria-label="查看详情" @click="showDetail(record)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button
                      v-if="record.status === 3"
                      class="table-action-button is-teal"
                      title="重试" aria-label="重试"
                      @click="retryRecord(record)"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                      </svg>
                    </button>
                    <button class="table-action-button is-neutral" title="导出" aria-label="导出" @click="exportRecord(record)">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 分页 -->
      <AppPagination
        v-if="pagination.total > 0"
        class="pagination-bridge"
        :pagination="pagination"
        :page-sizes="[10, 20, 50]"
        :show-pager="pagination.pages > 1"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <!-- 详情弹窗 -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="fixed inset-0 z-50 overflow-y-auto">
        <div class="fixed inset-0 bg-black/50" @click="showDetailModal = false"/>
        <div class="relative min-h-screen flex items-center justify-center p-4">
          <div class="relative bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <!-- 标题栏 -->
            <div class="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h3 class="text-lg font-semibold text-slate-900">结算单详情</h3>
                <p class="text-sm text-slate-500 font-mono">{{ detailData.settlementNo || currentRecord?.settlementNo }}</p>
              </div>
              <div class="flex items-center gap-3">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                  :class="getStatusClass(detailData.status)"
                >
                  <span class="w-2 h-2 rounded-full mr-1.5" :class="getStatusDotClass(detailData.status)"/>
                  {{ detailData.statusDesc || getStatusLabel(detailData.status) }}
                </span>
                <button class="p-1.5 text-slate-400 hover:text-slate-600" @click="showDetailModal = false">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
            </div>

            <!-- 内容区域 -->
            <div class="flex-1 overflow-y-auto min-h-0">
              <!-- 加载状态 -->
              <div v-if="detailLoading" class="p-12 text-center">
                <div class="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-4">
                  <svg class="w-6 h-6 text-primary-600 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                </div>
                <p class="text-slate-500">加载中...</p>
              </div>

              <!-- 详情内容 -->
              <template v-else>
                <!-- 基本信息卡片 -->
                <div class="px-6 py-4 border-b border-slate-200">
                  <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    基本信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">结算单号</div>
                      <div class="font-medium text-slate-900 font-mono text-sm">{{ detailData.settlementNo || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">来源类型</div>
                      <div class="font-medium text-slate-900">{{ getSourceTypeLabel(detailData.sourceType) }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">来源编号</div>
                      <div class="font-medium text-slate-900 font-mono text-sm">{{ detailData.orderNo || detailData.sourceId || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">分账规则</div>
                      <div class="font-medium text-slate-900">{{ detailData.ruleName || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">分账总金额</div>
                      <div class="font-medium text-slate-900 text-lg">¥{{ detailData.totalAmount?.toLocaleString() || '0' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">分账时间</div>
                      <div class="font-medium text-slate-900">{{ detailData.settleTime || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">创建时间</div>
                      <div class="font-medium text-slate-900">{{ detailData.createTime || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">备注</div>
                      <div class="font-medium text-slate-900">{{ detailData.remark || '-' }}</div>
                    </div>
                  </div>
                </div>

                <!-- 客户信息 -->
                <div v-if="detailData.customer" class="px-6 py-4 border-b border-slate-200">
                  <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                    </svg>
                    客户信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">客户姓名</div>
                      <div class="font-medium text-slate-900">{{ detailData.customer.name || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">客户编号</div>
                      <div class="font-medium text-slate-900 font-mono text-sm">{{ detailData.customer.customerNo || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">联系电话</div>
                      <div class="font-medium text-slate-900">{{ detailData.customer.phone || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">客户等级</div>
                      <div class="font-medium text-slate-900">{{ detailData.customer.levelName || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">客户状态</div>
                      <div class="font-medium text-slate-900">{{ detailData.customer.statusName || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">所属人</div>
                      <div class="font-medium text-slate-900">{{ detailData.customer.ownerUserName || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3 md:col-span-2">
                      <div class="text-xs text-slate-500 mb-1">地址</div>
                      <div class="font-medium text-slate-900">{{ detailData.customer.address || '' }}{{ detailData.customer.detailAddress || '' }}</div>
                    </div>
                  </div>
                </div>

                <!-- 方案信息 -->
                <div v-if="detailData.plan" class="px-6 py-4 border-b border-slate-200">
                  <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                    </svg>
                    方案信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">方案编号</div>
                      <div class="font-medium text-slate-900 font-mono text-sm">{{ detailData.plan.planNo || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">方案名称</div>
                      <div class="font-medium text-slate-900">{{ detailData.plan.name || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">总金额</div>
                      <div class="font-medium text-slate-900">¥{{ detailData.plan.totalAmount?.toLocaleString() || '0' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">应付金额</div>
                      <div class="font-medium text-slate-900">¥{{ detailData.plan.payableAmount?.toLocaleString() || '0' }}</div>
                    </div>
                  </div>
                  <!-- 订单列表 -->
                  <div v-if="detailData.plan.orders && detailData.plan.orders.length > 0" class="mt-4">
                    <h5 class="text-xs font-medium text-slate-600 mb-2">订单列表</h5>
                    <div class="data-table-shell">
                      <div class="data-table-scroll">
                        <table class="data-table text-sm">
                        <thead class="data-table-head">
                          <tr>
                            <th class="data-table-th">订单编号</th>
                            <th class="data-table-th">产品名称</th>
                            <th class="data-table-th">订单类型</th>
                            <th class="px-4 py-2 text-right text-xs font-medium text-slate-500">订单金额</th>
                            <th class="data-table-th">执行人员</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="order in detailData.plan.orders" :key="order.id" class="data-table-row">
                            <td class="data-table-td font-mono text-slate-900">{{ order.orderNo || '-' }}</td>
                            <td class="data-table-td text-slate-900">{{ order.productNameSnapshot || '-' }}</td>
                            <td class="data-table-td text-slate-600">{{ order.orderType === 'ONCE' ? '一次性服务' : '分期订单' }}</td>
                            <td class="data-table-td text-right font-medium text-slate-900">¥{{ order.amount?.toLocaleString() || '0' }}</td>
                            <td class="data-table-td text-slate-600">{{ order.executor?.displayName || '-' }}</td>
                          </tr>
                        </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 账单信息 -->
                <div v-if="detailData.billing" class="px-6 py-4 border-b border-slate-200">
                  <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                    账单信息
                  </h4>
                  <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">账单类型</div>
                      <div class="font-medium text-slate-900">{{ detailData.billing.billingType || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">期次</div>
                      <div class="font-medium text-slate-900">{{ detailData.billing.periodIndex ? `第${detailData.billing.periodIndex}期` : '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">应收金额</div>
                      <div class="font-medium text-slate-900">¥{{ detailData.billing.amount?.toLocaleString() || '0' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">已收金额</div>
                      <div class="font-medium text-slate-900">¥{{ detailData.billing.paidAmount?.toLocaleString() || '0' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">应收日期</div>
                      <div class="font-medium text-slate-900">{{ detailData.billing.dueDate || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">收款时间</div>
                      <div class="font-medium text-slate-900">{{ detailData.billing.paidTime || '-' }}</div>
                    </div>
                    <div class="bg-slate-50 rounded-lg p-3">
                      <div class="text-xs text-slate-500 mb-1">支付渠道</div>
                      <div class="font-medium text-slate-900">{{ detailData.billing.payChannel || '-' }}</div>
                    </div>
                  </div>
                </div>

                <!-- 分账明细 -->
                <div v-if="detailData.details && detailData.details.length > 0" class="px-6 py-4">
                  <h4 class="text-sm font-medium text-slate-700 mb-3 flex items-center">
                    <svg class="w-4 h-4 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    分账明细
                  </h4>
                  <div class="data-table-shell">
                    <div class="data-table-scroll">
                      <table class="data-table text-sm">
                      <thead class="data-table-head">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">角色</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">角色实例</th>
                          <th class="px-4 py-3 text-right text-xs font-medium text-slate-500">分成比例</th>
                          <th class="px-4 py-3 text-right text-xs font-medium text-slate-500">分账金额</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">状态</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-slate-500">结算时间</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="detail in detailData.details" :key="detail.id" class="data-table-row">
                          <td class="data-table-td text-slate-900">
                            <span class="status-badge bg-blue-100 text-blue-700">
                              {{ detail.roleName || '-' }}
                            </span>
                          </td>
                          <td class="data-table-td text-slate-900">
                            <template v-if="detail.referrer">
                              <div class="font-medium">{{ detail.referrer.name }}</div>
                              <div class="data-table-cell-meta mt-1">{{ detail.referrer.phone }}</div>
                            </template>
                            <template v-else-if="detail.franchisee">
                              <div class="font-medium">{{ detail.franchisee.name }}</div>
                              <div class="data-table-cell-meta mt-1">{{ detail.franchisee.leader }} {{ detail.franchisee.phone }}</div>
                            </template>
                            <template v-else>-</template>
                          </td>
                          <td class="data-table-td text-right text-slate-600">{{ detail.ratio }}%</td>
                          <td class="data-table-td text-right font-medium text-slate-900">¥{{ detail.amount?.toLocaleString() || '0' }}</td>
                          <td class="data-table-td">
                            <span class="status-badge" :class="getSettlementStatusClass(detail.status)">
                              {{ detail.statusDesc || getSettlementStatusLabel(detail.status) }}
                            </span>
                          </td>
                          <td class="data-table-td text-slate-600">{{ detail.settleTime || '-' }}</td>
                        </tr>
                      </tbody>
                      <tfoot class="bg-slate-100">
                        <tr>
                          <td colspan="3" class="px-4 py-3 text-sm font-medium text-slate-700 text-right">合计</td>
                          <td class="px-4 py-3 text-sm font-bold text-slate-900 text-right">
                            ¥{{ getTotalAmount(detailData.details)?.toLocaleString() || '0' }}
                          </td>
                          <td colspan="2"></td>
                        </tr>
                      </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- 底部按钮 -->
            <div class="flex-shrink-0 px-6 py-4 border-t border-slate-200 flex justify-end gap-3 bg-slate-50">
              <button class="btn-secondary" @click="showDetailModal = false">关闭</button>
              <button
                v-if="detailData.status === 3"
                class="btn-danger-secondary"
                @click="retryRecord(detailData)"
              >
                重新分账
              </button>
              <button class="btn-secondary" @click="exportRecord(detailData)">
                导出明细
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import { getCommissionSettlementPage, getCommissionSettlementDetail, retryCommissionRecord, exportCommissionRecord, getCommissionRuleList } from '@/api/commission'

// 来源类型
const sourceTypes = [
  { value: 'ORDER', label: '订单' },
  { value: 'BILLING', label: '账单' }
]

// 分账状态
const statusOptions = [
  { value: 0, label: '待分账' },
  { value: 1, label: '分账中' },
  { value: 2, label: '分账完成' },
  { value: 3, label: '分账失败' },
  { value: 4, label: '已取消' }
]

// 搜索表单
const searchForm = reactive({
  settlementNo: '',
  sourceId: '',
  orderId: '',
  sourceType: '',
  status: '',
  startTime: '',
  endTime: '',
  ruleId: '',
  customerId: '',
  referrerId: '',
  franchiseeId: ''
})

const loading = ref(false)
const recordList = ref([])
const pagination = reactive({
  current: 1,
  size: 20,
  total: 0,
  pages: 0
})

// 高级筛选
const showAdvancedFilter = ref(false)

// 规则列表
const ruleList = ref([])

// 详情弹窗
const showDetailModal = ref(false)
const currentRecord = ref(null)
const detailLoading = ref(false)
const detailData = ref({})

const getSourceTypeLabel = (type) => {
  const found = sourceTypes.find(t => t.value === type)
  return found ? found.label : type
}

const getSourceTypeClass = (type) => {
  const classes = {
    ORDER: 'bg-blue-100 text-blue-700',
    BILLING: 'bg-purple-100 text-purple-700'
  }
  return classes[type] || 'bg-slate-100 text-slate-700'
}

const getStatusLabel = (status) => {
  const found = statusOptions.find(s => s.value === status)
  return found ? found.label : status
}

const getStatusClass = (status) => {
  const classes = {
    0: 'bg-slate-100 text-slate-600',
    1: 'bg-blue-100 text-blue-700',
    2: 'bg-green-100 text-green-700',
    3: 'bg-red-100 text-red-700',
    4: 'bg-slate-100 text-slate-500'
  }
  return classes[status] || 'bg-slate-100 text-slate-600'
}

const getStatusDotClass = (status) => {
  const classes = {
    0: 'bg-slate-500',
    1: 'bg-blue-500 animate-pulse',
    2: 'bg-green-500',
    3: 'bg-red-500',
    4: 'bg-slate-400'
  }
  return classes[status] || 'bg-slate-500'
}

const getSettlementStatusLabel = (status) => {
  const labels = {
    0: '待结算',
    1: '已结算',
    2: '已取消'
  }
  return labels[status] ?? status
}

const getSettlementStatusClass = (status) => {
  const classes = {
    0: 'bg-slate-100 text-slate-600',
    1: 'bg-green-100 text-green-700',
    2: 'bg-slate-100 text-slate-500'
  }
  return classes[status] || 'bg-slate-100 text-slate-600'
}

// 计算分账明细总金额
const getTotalAmount = (details) => {
  if (!details || !Array.isArray(details)) return 0
  return details.reduce((sum, detail) => sum + (detail.amount || 0), 0)
}

// 获取记录列表
const fetchRecords = async () => {
  loading.value = true
  try {
    const params = {
      settlementNo: searchForm.settlementNo || undefined,
      sourceId: searchForm.sourceId ? Number(searchForm.sourceId) : undefined,
      orderId: searchForm.orderId ? Number(searchForm.orderId) : undefined,
      sourceType: searchForm.sourceType || undefined,
      status: searchForm.status !== '' ? Number(searchForm.status) : undefined,
      startTime: searchForm.startTime || undefined,
      endTime: searchForm.endTime || undefined,
      ruleId: searchForm.ruleId ? Number(searchForm.ruleId) : undefined,
      customerId: searchForm.customerId ? Number(searchForm.customerId) : undefined,
      referrerId: searchForm.referrerId ? Number(searchForm.referrerId) : undefined,
      franchiseeId: searchForm.franchiseeId ? Number(searchForm.franchiseeId) : undefined,
      pageNum: pagination.current,
      pageSize: pagination.size
    }
    const data = await getCommissionSettlementPage(params)
    recordList.value = data.records || []
    pagination.current = data.current
    pagination.size = data.size
    pagination.total = data.total
    pagination.pages = data.pages
  } catch (error) {
    ElMessage.error(error.message || '获取结算单失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.current = 1
  fetchRecords()
}

const resetSearch = () => {
  searchForm.settlementNo = ''
  searchForm.sourceId = ''
  searchForm.orderId = ''
  searchForm.sourceType = ''
  searchForm.status = ''
  searchForm.startTime = ''
  searchForm.endTime = ''
  searchForm.ruleId = ''
  searchForm.customerId = ''
  searchForm.referrerId = ''
  searchForm.franchiseeId = ''
  pagination.current = 1
  fetchRecords()
}

const handlePageChange = (page) => {
  if (page < 1 || page > pagination.pages) return
  pagination.current = page
  fetchRecords()
}

const handlePageSizeChange = (size) => {
  pagination.size = size
  pagination.current = 1
  fetchRecords()
}

// 查看详情
const showDetail = async (record) => {
  currentRecord.value = record
  showDetailModal.value = true
  detailLoading.value = true
  detailData.value = {}

  try {
    const data = await getCommissionSettlementDetail(record.id)
    detailData.value = data || {}
  } catch (error) {
    ElMessage.error(error.message || '获取详情失败')
  } finally {
    detailLoading.value = false
  }
}

// 重试
const retryRecord = async (record) => {
  try {
    await retryCommissionRecord(record.id)
    ElMessage.success('已加入重试队列')
    showDetailModal.value = false
    fetchRecords()
  } catch (error) {
    ElMessage.error(error.message || '重试失败')
  }
}

// 导出
const exportRecord = async (record) => {
  try {
    await exportCommissionRecord(record.id)
    ElMessage.success('导出成功')
  } catch (error) {
    ElMessage.error(error.message || '导出失败')
  }
}

// 加载规则列表
const loadRules = async () => {
  try {
    const data = await getCommissionRuleList({ page: 1, size: 100 })
    ruleList.value = data.records || []
  } catch (error) {
    console.error('加载规则列表失败', error)
  }
}

onMounted(() => {
  fetchRecords()
  loadRules()
})
</script>
